import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { useAuth } from '@/contexts/AuthContext';
import { trackCheckoutInitiation } from '@/services/meta-events';
import { useAnalytics } from '@/hooks/useAnalytics';
import { handleSubscriptionPurchase } from '@/services/payment-controller';
import { toast } from 'sonner';

interface PaywallPurchaseButtonProps {
  planId: string;
  planName: string;
  planPrice: number;
  period: 'mensal' | 'anual';
  text: string;
  className?: string;
}

export const PaywallPurchaseButton: React.FC<PaywallPurchaseButtonProps> = ({
  planId,
  planName,
  planPrice,
  period,
  text,
  className
}) => {
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { trackCheckoutInitiated } = useAnalytics();

  // Map plan names to system plan IDs
  const mapPlanNameToId = (name: string): string => {
    const normalized = name.toLowerCase();
    if (normalized.includes('flerte')) return 'flerte';
    if (normalized.includes('desejo')) return 'desejo';
    if (normalized.includes('atracao') || normalized.includes('atração')) return 'atracao';
    if (normalized.includes('paixao') || normalized.includes('paixão')) return 'paixao';
    return normalized;
  };

  const handlePurchase = async () => {
    setLoading(true);
    
    try {
      const platform = Capacitor.getPlatform();
      const isNative = platform === 'ios' || platform === 'android';
      const billingPeriod = period === 'mensal' ? 'monthly' : 'annual';
      const systemPlanId = mapPlanNameToId(planName);
      
      // Track checkout initiation
      const userEmail = user?.email || '';
      const userId = user?.id || '';

      if (userEmail) {
        // Meta tracking
        await trackCheckoutInitiation(
          userEmail,
          planPrice,
          'BRL',
          `${planName} ${period === 'mensal' ? 'Monthly' : 'Annual'} Plan`,
          userId
        );

        // Mixpanel tracking
        try {
          trackCheckoutInitiated(
            `${planName} ${period === 'mensal' ? 'Monthly' : 'Annual'} Plan`,
            planPrice,
            billingPeriod,
            'BRL'
          );
        } catch (error) {
          console.error('Failed to track Mixpanel checkout event:', error);
        }
      }

      if (isNative) {
        // Mobile: Use RevenueCat (same logic as SubscriptionPlans)
        try {
          const offerings = await Purchases.getOfferings();
          
          // Find the package that matches our plan
          let targetPackage = null;
          
          // Look through all offerings for a package with matching identifier
          for (const [key, offering] of Object.entries(offerings.all)) {
            for (const pkg of offering.availablePackages) {
              // Match based on plan ID or product ID
              if (pkg.product.identifier.includes(planId) || 
                  pkg.identifier.includes(planId) ||
                  pkg.product.identifier.includes(systemPlanId) ||
                  pkg.product.identifier.includes(planName.toLowerCase())) {
                targetPackage = pkg;
                break;
              }
            }
            if (targetPackage) break;
          }

          if (targetPackage) {
            console.log(`${platform.toUpperCase()}: Attempting RevenueCat purchase with package:`, targetPackage);
            const result = await Purchases.purchasePackage({ aPackage: targetPackage });
            console.log('Purchase successful:', result);
            
            if (result.customerInfo && result.customerInfo.entitlements) {
              console.log('Customer entitlements:', result.customerInfo.entitlements);
              toast.success('Compra realizada com sucesso!');
              // Refresh subscription status
              window.location.reload();
            }
          } else {
            console.error(`${platform.toUpperCase()}: No RevenueCat package found for plan ${planId}`);
            toast.error('Plano temporariamente indisponível. Por favor, atualize o aplicativo.');
          }
        } catch (e: any) {
          console.error('RevenueCat purchase error:', e);
          if (!e?.userCancelled) {
            toast.error('Erro ao processar compra. Tente novamente.');
          }
        }
      } else {
        // Web: Use payment-controller (same logic as SubscriptionPlans)
        try {
          const result = await handleSubscriptionPurchase(systemPlanId as any, billingPeriod as any);
          
          if (result.success) {
            toast.success('Redirecionando para o pagamento...');
          } else {
            toast.error(result.error || 'Erro ao processar compra. Tente novamente.');
          }
        } catch (error) {
          console.error('Web payment error:', error);
          toast.error('Erro ao processar compra. Tente novamente.');
        }
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error('Erro ao processar compra. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={handlePurchase}
      disabled={loading}
      className={className}
    >
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Processando...
        </>
      ) : (
        text
      )}
    </Button>
  );
};